import { initHeroChat } from './hero-chat.js';
import { landingStats, landingDashboard, landingFooterPopover } from './landing-sections.js';

const registerLandingComponents = () => {
    window.Alpine.data('landingStats', landingStats);
    window.Alpine.data('landingDashboard', landingDashboard);
    window.Alpine.data('landingFooterPopover', landingFooterPopover);
};

if (window.Alpine) {
    registerLandingComponents();
} else {
    document.addEventListener('alpine:init', registerLandingComponents, { once: true });
}

/**
 * Suppress CSS transitions for the frame in which the dark class flips, so a theme change swaps every
 * colour at once instead of starting hundreds of transitions (borders, shadows, text) together.
 */
const suppressTransitionsOnThemeChange = () => {
    const root = document.documentElement;
    let isDark = root.classList.contains('dark');
    let releaseTimer = null;

    new MutationObserver(() => {
        const nowDark = root.classList.contains('dark');
        if (nowDark === isDark) {
            return;
        }
        isDark = nowDark;
        root.classList.add('theme-switching');
        window.clearTimeout(releaseTimer);
        // Release after the new colours have been painted.
        requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-switching')));
        releaseTimer = window.setTimeout(() => root.classList.remove('theme-switching'), 120);
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
};
suppressTransitionsOnThemeChange();

console.log(
    '%cهشدار!  در این بخش چیزی وارد یا جای‌گذاری نکنید.',
    'background:#b91c1c;color:#fff;font-size:24px;font-weight:800;padding:8px 12px;border-radius:6px;'
);
console.log(
    '%cاین بخش مخصوص توسعه‌دهندگان است. وارد کردن یا جای‌گذاری کد ناشناس می‌تواند امنیت حساب شما را به خطر بیندازد.',
    'color:#b91c1c;font-size:14px;font-weight:600;'
);

const mountedLightPillars = new WeakMap();
let lightPillarModulePromise = null;
const mountedHeroChats = new WeakMap();
const mountedBentoGrids = new WeakMap();
let bentoGridModulePromise = null;
const mountedGrainGradients = new WeakMap();
let grainGradientModulePromise = null;

const mountGrainGradients = async () => {
    const containers = [...document.querySelectorAll('[data-grain-gradient]')];

    if (containers.length === 0) {
        return;
    }

    try {
        grainGradientModulePromise ??= import('./grain-gradient.js');
        const { initGrainGradient } = await grainGradientModulePromise;

        await Promise.all(containers.map(async (container) => {
            if (container.isConnected && !mountedGrainGradients.has(container)) {
                const pendingCleanup = () => {};
                mountedGrainGradients.set(container, pendingCleanup);
                const cleanup = await initGrainGradient(container);

                if (container.isConnected && mountedGrainGradients.get(container) === pendingCleanup) {
                    mountedGrainGradients.set(container, cleanup);
                } else {
                    cleanup();
                }
            }
        }));
    } catch {
        // The hero's CSS background remains visible if the shader cannot load.
    }
};

const cleanupGrainGradients = () => {
    document.querySelectorAll('[data-grain-gradient]').forEach((container) => {
        mountedGrainGradients.get(container)?.();
        mountedGrainGradients.delete(container);
    });
};

const shouldMountLightPillar = () => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    return !prefersReducedMotion;
};

const setLandingBackgroundReady = () => {
    document.documentElement.dataset.landingBackground = 'ready';

    if (document.documentElement.dataset.landingHeroReady !== 'true') {
        window.requestAnimationFrame(() => {
            document.documentElement.dataset.landingHeroReady = 'true';
        });
    }
};

const mountLightPillars = async () => {
    const containers = [...document.querySelectorAll('[data-light-pillar]')];

    if (containers.length === 0) {
        setLandingBackgroundReady();

        return;
    }

    if (!shouldMountLightPillar()) {
        setLandingBackgroundReady();

        return;
    }

    try {
        lightPillarModulePromise ??= import('./light-pillar.js');
        const { initLightPillar } = await lightPillarModulePromise;

        containers.forEach((container) => {
            if (container.isConnected && !mountedLightPillars.has(container)) {
                mountedLightPillars.set(container, initLightPillar(container));
            }
        });
    } catch {
        cleanupLightPillars();
    } finally {
        setLandingBackgroundReady();
    }
};

const cleanupLightPillars = () => {
    document.querySelectorAll('[data-light-pillar]').forEach((container) => {
        mountedLightPillars.get(container)?.();
        mountedLightPillars.delete(container);
    });
};

const mountHeroChats = () => {
    const containers = [...document.querySelectorAll('[data-hero-chat]')];

    if (containers.length === 0) {
        return;
    }

    containers.forEach((container) => {
        if (container.isConnected && !mountedHeroChats.has(container)) {
            mountedHeroChats.set(container, initHeroChat(container));
        }
    });
};

const cleanupHeroChats = () => {
    document.querySelectorAll('[data-hero-chat]').forEach((container) => {
        mountedHeroChats.get(container)?.();
        mountedHeroChats.delete(container);
    });
};

const mountBentoGrids = () => {
    const containers = [...document.querySelectorAll('[data-bento-grid]')];

    if (containers.length === 0) {
        return;
    }

    containers.forEach((container) => {
        if (!container.isConnected || mountedBentoGrids.has(container)) {
            return;
        }

        let observer = null;
        let cancelled = false;
        let cleanupGrid = null;
        mountedBentoGrids.set(container, () => {
            cancelled = true;
            observer?.disconnect();
            cleanupGrid?.();
        });

        const mount = async () => {
            observer?.disconnect();
            try {
                bentoGridModulePromise ??= import('./bento-grid.js');
                const { initBentoGrid } = await bentoGridModulePromise;
                if (!cancelled && container.isConnected) {
                    cleanupGrid = initBentoGrid(container);
                }
            } catch {
                if (!cancelled) {
                    mountedBentoGrids.delete(container);
                }
            }
        };

        if (typeof IntersectionObserver === 'undefined') {
            mount();
            return;
        }

        observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                mount();
            }
        }, { rootMargin: '100px' });
        observer.observe(container);
    });
};

const cleanupBentoGrids = () => {
    document.querySelectorAll('[data-bento-grid]').forEach((container) => {
        mountedBentoGrids.get(container)?.();
        mountedBentoGrids.delete(container);
    });
};

const handlePageShow = (event) => {
    if (event.persisted) {
        cleanupHeroChats();
        cleanupBentoGrids();
    }

    mountLightPillars();
    mountHeroChats();
    mountBentoGrids();
    mountGrainGradients();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountLightPillars, { once: true });
    document.addEventListener('DOMContentLoaded', mountHeroChats, { once: true });
    document.addEventListener('DOMContentLoaded', mountBentoGrids, { once: true });
    document.addEventListener('DOMContentLoaded', mountGrainGradients, { once: true });
} else {
    mountLightPillars();
    mountHeroChats();
    mountBentoGrids();
    mountGrainGradients();
}

document.addEventListener('livewire:navigated', mountLightPillars);
document.addEventListener('livewire:navigating', cleanupLightPillars);
document.addEventListener('livewire:navigated', mountHeroChats);
document.addEventListener('livewire:navigating', cleanupHeroChats);
document.addEventListener('livewire:navigated', mountBentoGrids);
document.addEventListener('livewire:navigating', cleanupBentoGrids);
document.addEventListener('livewire:navigated', mountGrainGradients);
document.addEventListener('livewire:navigating', cleanupGrainGradients);
window.addEventListener('pageshow', handlePageShow);
window.addEventListener('pagehide', (event) => {
    if (!event.persisted) {
        cleanupLightPillars();
        cleanupHeroChats();
        cleanupBentoGrids();
        cleanupGrainGradients();
    }
});
